import {computed, inject, Injectable, signal} from '@angular/core';
import {firstValueFrom} from 'rxjs';
import {ConsultaSei, ProcessoSei} from '@core/models/processo-sei';
import {ProcessoSeiService} from '@core/services/processo-sei.service';
import {SeiService} from '@core/services/sei.service';

export type Resultado = ConsultaSei & {ms: number};
export type Situacao = 'na-caixa' | 'outra-unidade' | 'concluido' | 'nao-encontrado' | 'outro' | 'pendente';

export interface EstadoBot {
  /** Caixas (unidades exatas) do SEI da conferência. */
  caixas: string[];
  fila: string[];
  resultados: Record<string, Resultado>;
  gastoMs: number;
  /** Leitura de cada caixa no SEI (todos os números que estão nela). */
  caixasLidas: Record<string, {numeros: string[]; quando: string}>;
  /** Grava na coluna Localização a(s) unidade(s) do SEI onde o processo está. */
  atualizarLocalizacao: boolean;
}

export interface LinhaBot {
  n: ProcessoSei;
  numero: string;
  r: Resultado | undefined;
  situacao: Situacao;
  /** Em qual(is) das caixas o processo está. */
  emCaixas: string[];
  /** Localização a gravar (unidades do SEI, só o nome), ou null se não muda. */
  novaLocalizacao: string | null;
}

/** Processo que está numa das caixas do SEI e não tem notificação no SisNot. */
export interface SoNoSei {
  numero: string;
  caixas: string[];
}

export const ROTULO_SEI: Record<string, string> = {
  aberto: 'Aberto', concluido: 'Concluído', nao_encontrado: 'Não encontrado', indefinido: 'Não identificado',
  anexado: 'Anexado', sobrestado: 'Sobrestado', erro: 'Erro', deslogado: 'Sem sessão no SEI',
};

export const ROTULO_SITUACAO: Record<string, string> = {
  'na-caixa': 'Na caixa', 'outra-unidade': 'Em outra unidade', concluido: 'Concluído no SEI',
  'nao-encontrado': 'Não encontrado no SEI', outro: 'Anexado / sobrestado / erro', pendente: 'Na fila',
};

/**
 * Bot SisNot ↔ SEI (o estado e a pesquisa ficam aqui, não na tela): a pessoa
 * pode ir para outras telas do SisNot e o bot continua pesquisando. Fechar ou
 * recarregar a página para (o andamento fica guardado neste navegador e dá pra
 * continuar de onde parou).
 *
 * "Conferir" faz as duas etapas, uma depois da outra:
 * 1. SisNot → SEI: cada processo das notificações é pesquisado no SEI (a
 *    extensão usa a sessão do SEI do usuário, um por vez com uma pausa curta) e
 *    o relatório diz se está numa das caixas (unidades exatas), em outra unidade, concluído…
 * 2. SEI → SisNot: no fim, a extensão lê cada caixa no SEI (uma por vez) e o bot
 *    mostra os processos que estão lá e não têm notificação no SisNot.
 *
 * A pesquisa rápida abre o processo, e o SEI tira o vermelho (não visualizado)
 * dos processos da unidade em que o usuário está. Por isso, antes da etapa 1,
 * o bot lê a lista da caixa da unidade do usuário (ler a lista não abre nada) e
 * não pesquisa os vermelhos: eles estão abertos nessa unidade, que é o que o bot
 * precisa saber. Se a sessão do SEI mudar de unidade no meio, ele lê a nova.
 */
@Injectable({providedIn: 'root'})
export class BotSeiService {
  private static readonly CHAVE = 'sisnot:bot-sei';
  private static readonly NUMERO_SEI = /\d{3}\.\d{7,8}\/\d{4}-\d{2}/;

  private sei = inject(SeiService);
  private service = inject(ProcessoSeiService);

  readonly processos = signal<ProcessoSei[]>([]);
  readonly carregando = signal(false);
  readonly erroCarga = signal<string | null>(null);
  readonly estado = signal<EstadoBot>(this.lerSalvo());
  readonly rodando = signal(false);
  readonly atual = signal<string | null>(null);
  /** Caixa que está sendo lida no SEI agora (etapa 2), ou null. */
  readonly lendoCaixa = signal<string | null>(null);
  readonly erroCaixa = signal<string | null>(null);
  readonly gravandoLocalizacao = signal(false);
  readonly localizacoesGravadas = signal(0);
  readonly erroLocalizacao = signal<string | null>(null);
  /** Lendo a caixa da unidade do usuário antes de pesquisar (pra não tirar o vermelho). */
  readonly protegendo = signal(false);
  // vermelhos da caixa da unidade em que a sessão do SEI está
  private protegidos: {unidade: string; vermelhos: Set<string>} | null = null;
  private carregado = false;

  /** Cada notificação da conferência com o resultado do número dela. */
  readonly linhas = computed<LinhaBot[]>(() => {
    const e = this.estado();
    if (!e.fila.length) return [];
    const naFila = new Set(e.fila);
    return this.processos()
      .filter(n => naFila.has(this.numero(n)))
      .map(n => {
        const numero = this.numero(n);
        const r = e.resultados[numero];
        const emCaixas = r?.status === 'aberto' ? e.caixas.filter(c => r.unidades.some(u => this.naCaixa(u, c))) : [];
        const loc = this.localizacaoDoSei(r);
        const novaLocalizacao = loc && loc !== (n.localizacao ?? '').trim() ? loc : null;
        return {n, numero, r, emCaixas, novaLocalizacao, situacao: this.situacao(r, emCaixas)};
      });
  });

  readonly contagem = computed(() => {
    const c: Record<Situacao, number> = {'na-caixa': 0, 'outra-unidade': 0, concluido: 0, 'nao-encontrado': 0, outro: 0, pendente: 0};
    this.linhas().forEach(l => c[l.situacao]++);
    return c;
  });

  readonly progresso = computed(() => {
    const e = this.estado();
    const total = e.fila.length;
    const feitos = e.fila.filter(n => e.resultados[n]).length;
    const media = feitos ? e.gastoMs / feitos : 0;
    const faltaSeg = media * (total - feitos) / 1000;
    return {
      total, feitos, pct: total ? Math.round(feitos / total * 100) : 0,
      decorrido: this.duracao(e.gastoMs / 1000),
      media: feitos ? `${(media / 1000).toLocaleString('pt-BR', {maximumFractionDigits: 1})} s` : '—',
      falta: feitos && feitos < total ? `~${this.duracao(faltaSeg)}` : (total && feitos >= total ? 'concluído' : '—'),
    };
  });
  readonly pendente = computed(() => this.progresso().total > 0 && this.progresso().feitos < this.progresso().total);
  /** Notificações cuja Localização é diferente do que o SEI diz (a gravar). */
  readonly localizacoesPendentes = computed(() => this.linhas().filter(l => l.novaLocalizacao));

  /** Todas as caixas da conferência já foram lidas no SEI? */
  readonly caixasLidasTodas = computed(() => {
    const e = this.estado();
    return e.caixas.length > 0 && e.caixas.every(c => !!e.caixasLidas[c]);
  });
  /** Alguma das caixas da conferência já foi lida (a aba "Só no SEI" aparece)? */
  readonly algumaCaixaLida = computed(() => this.estado().caixas.some(c => !!this.estado().caixasLidas[c]));

  /** Processos que estão nas caixas lidas do SEI e não têm notificação no SisNot. */
  readonly soNoSei = computed<SoNoSei[]>(() => {
    const e = this.estado();
    const noSisnot = new Set(this.processos().map(p => this.numero(p)));
    const porNumero = new Map<string, string[]>();
    e.caixas.forEach(c => (e.caixasLidas[c]?.numeros ?? []).forEach(n => {
      if (!noSisnot.has(n)) porNumero.set(n, [...(porNumero.get(n) ?? []), c]);
    }));
    return [...porNumero.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([numero, caixas]) => ({numero, caixas}));
  });

  /** Siglas de unidade vistas nos resultados (sugestões para a caixa). */
  readonly unidadesVistas = computed(() => {
    const u = new Set<string>();
    Object.values(this.estado().resultados).forEach(r => r.unidades.forEach(x => {
      const s = this.sigla(x);
      if (s) u.add(s);
    }));
    return [...u].sort();
  });

  /** Carrega as notificações com processo SEI (uma vez; a tela chama ao abrir). */
  carregar() {
    if (this.carregado || this.carregando()) return;
    this.carregando.set(true);
    this.service.processos().subscribe({
      next: lista => { this.processos.set(lista); this.carregando.set(false); this.carregado = true; },
      error: () => { this.erroCarga.set('Não foi possível carregar os processos do SisNot.'); this.carregando.set(false); },
    });
  }

  iniciar(caixas: string[], fila: string[], atualizarLocalizacao: boolean) {
    this.localizacoesGravadas.set(0);
    this.estado.update(e => ({...e, caixas, fila, resultados: {}, gastoMs: 0, caixasLidas: {}, atualizarLocalizacao}));
    this.salvar();
    this.rodar();
  }

  /**
   * Troca as caixas sem pesquisar os processos de novo (o SEI já disse onde cada
   * um está) e lê no SEI as caixas que ainda não foram lidas (etapa 2).
   */
  aplicarCaixas(caixas: string[]) {
    this.estado.update(e => ({...e, caixas}));
    this.salvar();
    if (!this.pendente()) this.lerCaixas();
  }

  continuar() {
    this.rodar();
  }

  /** "Apenas consultar" x "Consultar e atualizar a Localização" (vale também no meio da pesquisa). */
  definirAtualizarLocalizacao(atualizar: boolean) {
    this.estado.update(e => ({...e, atualizarLocalizacao: atualizar}));
    this.salvar();
  }

  pausar() {
    this.rodando.set(false);
  }

  limpar() {
    this.estado.update(e => ({...e, fila: [], resultados: {}, gastoMs: 0, caixasLidas: {}}));
    this.salvar();
  }

  /** SEI → SisNot (etapa 2): lê no SEI, uma por vez, as caixas ainda não lidas (todas as páginas). */
  async lerCaixas() {
    if (this.lendoCaixa()) return;
    this.erroCaixa.set(null);
    const erros: string[] = [];
    for (const unidade of this.estado().caixas) {
      if (this.estado().caixasLidas[unidade]) continue;
      this.lendoCaixa.set(unidade);
      this.sei.andamento.set(`Abrindo a caixa ${unidade} no SEI…`);
      const r = await this.sei.lerCaixa(unidade);
      if (r.ok) {
        this.estado.update(e => ({...e, caixasLidas: {...e.caixasLidas, [unidade]: {numeros: r.numeros, quando: new Date().toISOString()}}}));
        this.salvar();
      } else {
        erros.push(`${unidade}: ${r.erro || 'não deu para ler a caixa no SEI'}`);
      }
    }
    this.lendoCaixa.set(null);
    this.sei.andamento.set('');
    if (erros.length) this.erroCaixa.set(erros.join(' · '));
  }

  private async rodar() {
    if (this.rodando()) return;
    if (this.sei.estado() !== 'logado') {
      alert('Você não está conectado ao SEI. Entre no SEI (botão do SEI no topo) e depois clique em "Continuar".');
      return;
    }
    this.rodando.set(true);
    if (!(await this.protegerVermelhos())) {
      this.rodando.set(false);
      return;
    }
    for (const numero of this.estado().fila) {
      if (!this.rodando()) break;
      if (this.estado().resultados[numero]) continue;
      this.atual.set(numero);
      // vermelho na unidade do usuário: não abre (o SEI tiraria o vermelho)
      if (this.protegidos?.vermelhos.has(numero)) {
        const r: Resultado = {status: 'aberto', unidades: [this.protegidos.unidade], naoAberto: true, ms: 0};
        this.estado.update(e => ({...e, resultados: {...e.resultados, [numero]: r}}));
        this.salvar();
        continue;
      }
      const t0 = Date.now();
      const r = await this.sei.consultar(numero);
      // a sessão do SEI mudou de unidade (a pessoa trocou no SEI): lê a caixa da nova
      if (r.unidadeSessao && this.protegidos && r.unidadeSessao !== this.protegidos.unidade) {
        this.protegidos = null;
        if (!(await this.protegerVermelhos())) break;
      }
      if (r.status === 'deslogado') {
        this.rodando.set(false);
        this.atual.set(null);
        alert(`Bot SisNot ↔ SEI pausado: ${r.erro || 'a sessão do SEI caiu'}.\n\nEntre no SEI e clique em "Continuar" na tela do Bot.`);
        return;
      }
      // pausa curta: não sobrecarregar o SEI
      await new Promise(ok => setTimeout(ok, 250));
      const ms = Date.now() - t0;
      this.estado.update(e => ({...e, resultados: {...e.resultados, [numero]: {...r, ms}}, gastoMs: e.gastoMs + ms}));
      this.salvar();
      // grava a Localização aos poucos (de 25 em 25), não só no fim
      if (this.estado().atualizarLocalizacao && this.localizacoesPendentes().length >= 25) await this.gravarLocalizacoes();
    }
    if (this.estado().atualizarLocalizacao) await this.gravarLocalizacoes();
    this.rodando.set(false);
    this.atual.set(null);
    // etapa 2: terminou de pesquisar os processos (não foi pausa) -> lê as caixas no SEI
    if (!this.pendente() && this.progresso().total) await this.lerCaixas();
  }

  /**
   * Grava na Localização das notificações as unidades do SEI (só onde mudou).
   * O backend registra cada mudança no histórico da notificação.
   */
  async gravarLocalizacoes() {
    const pendentes = this.localizacoesPendentes();
    if (!pendentes.length || this.gravandoLocalizacao()) return;
    this.gravandoLocalizacao.set(true);
    this.erroLocalizacao.set(null);
    try {
      for (let i = 0; i < pendentes.length; i += 200) {
        const lote = pendentes.slice(i, i + 200).map(l => ({id: l.n.id, localizacao: l.novaLocalizacao!}));
        const r = await firstValueFrom(this.service.atualizarLocalizacoes(lote));
        this.localizacoesGravadas.update(t => t + r.atualizadas);
        // a lista local passa a ter o valor gravado (a linha sai dos pendentes)
        const gravadas = new Map(lote.map(x => [x.id, x.localizacao]));
        this.processos.update(lista => lista.map(p => gravadas.has(p.id) ? {...p, localizacao: gravadas.get(p.id)!} : p));
      }
    } catch (e: any) {
      this.erroLocalizacao.set(e?.status === 403
        ? 'Sem permissão para editar notificações: a Localização não foi gravada.'
        : 'Não deu para gravar a Localização agora; tente de novo pelo botão.');
    } finally {
      this.gravandoLocalizacao.set(false);
    }
  }

  /**
   * Localização segundo o SEI: as unidades onde o processo está aberto (ou a do
   * sobrestamento), só o nome, sem "(atribuído para …)", separadas por vírgula.
   * Concluído, anexado, não encontrado…: não muda a Localização (null).
   */
  localizacaoDoSei(r: Resultado | undefined): string | null {
    if (!r || (r.status !== 'aberto' && r.status !== 'sobrestado')) return null;
    const siglas = [...new Set(r.unidades.map(u => this.sigla(u)).filter(Boolean))];
    return siglas.length ? siglas.join(', ').slice(0, 255) : null;
  }

  /**
   * Lê a caixa da unidade em que o usuário está no SEI e guarda os vermelhos.
   * Sem conseguir ler, pergunta se continua mesmo assim (pode tirar o vermelho).
   */
  private async protegerVermelhos(): Promise<boolean> {
    if (this.protegidos) return true;
    this.protegendo.set(true);
    this.sei.andamento.set('Lendo a caixa da sua unidade no SEI (pra não tirar o vermelho dos processos)…');
    const r = await this.sei.lerCaixa('');
    this.protegendo.set(false);
    this.sei.andamento.set('');
    if (r.ok) {
      this.protegidos = {unidade: r.unidade, vermelhos: new Set(r.vermelhos)};
      return true;
    }
    return confirm(`Não deu para ler a caixa da sua unidade no SEI (${r.erro || 'sem resposta'}).\n\n` +
      'Se continuar, os processos em vermelho da sua unidade que o bot pesquisar deixam de ficar vermelhos. Continuar mesmo assim?');
  }

  /**
   * Excel: CSV com ; e BOM (abre direto no Excel com acentos). `filtradas`: só
   * as linhas que a tela está mostrando (filtros das colunas e aba).
   */
  baixar(filtradas?: LinhaBot[]) {
    const e = this.estado();
    const linhas: string[][] = [['Nº NOT', 'Data NOT', 'Superintendência', 'Concessionária', 'Status no SisNot', 'Fase no SisNot',
      'Processo SEI', 'Situação no SEI', 'Unidade(s) no SEI', `Em qual caixa (${e.caixas.join(', ')})`, 'Resultado']];
    (filtradas ?? this.linhas()).forEach(l => linhas.push([
      l.n.numeroNotificacao ?? '', this.dataBr(l.n.dataNotificacao), l.n.superintendencia ?? '', l.n.concessionaria ?? '',
      l.n.status ?? '', l.n.fase ?? '', l.numero, l.r ? (ROTULO_SEI[l.r.status] ?? l.r.status) : 'Na fila',
      l.r ? ((l.r.unidades.join(' | ') || l.r.erro || '') + (l.r.naoAberto ? ' (não visualizado: o bot não abriu pra manter o vermelho)' : '')) : '',
      l.emCaixas.join(' | ') || (l.r ? 'Nenhuma' : ''),
      ROTULO_SITUACAO[l.situacao],
    ]));
    // SEI → SisNot: os que estão nas caixas e não têm notificação (vão junto quando não há filtro)
    if (!filtradas) this.soNoSei().forEach(x => linhas.push(['', '', '', '', 'Sem notificação no SisNot', '', x.numero, 'Aberto',
      x.caixas.join(' | '), x.caixas.join(' | '), 'Só no SEI (sem notificação no SisNot)']));
    // texto começando com = + - @ viraria fórmula no Excel: vai com ' na frente
    const celula = (c: string) => `"${String(c).replace(/^([=+\-@\t\r])/, "'$1").replace(/"/g, '""')}"`;
    const csv = linhas.map(l => l.map(celula).join(';')).join('\r\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob(['﻿' + csv], {type: 'text/csv;charset=utf-8'}));
    a.download = `bot_sisnot_sei_${e.caixas.length === 1 ? e.caixas[0] : e.caixas.length + '_caixas'}_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 2000);
  }

  numero(p: ProcessoSei): string {
    return p.processo.match(BotSeiService.NUMERO_SEI)?.[0] ?? p.processo;
  }

  // "ARTESP-SUROD-SUP (atribuído para 100218405)" -> "ARTESP-SUROD-SUP"
  sigla(unidade: string): string {
    return (unidade.match(/[A-Z][A-Z0-9_]*(?:-[A-Z0-9_]+)+/) || [''])[0];
  }

  /**
   * A unidade é a caixa? Só a unidade exata (subunidade é outra caixa no SEI).
   * O SEI às vezes mostra a sigla sem o "ARTESP-" (SUROD-GERER-COGEP-NOT):
   * ARTESP-SUROD-GERER-COGEP-NOT e SUROD-GERER-COGEP-NOT são a mesma unidade.
   */
  naCaixa(unidade: string, caixa: string): boolean {
    const sem = (s: string) => s.replace(/^ARTESP-/, '');
    const c = sem(caixa);
    return !!c && sem(this.sigla(unidade)) === c;
  }

  /** A unidade é alguma das caixas? */
  emAlgumaCaixa(unidade: string, caixas: string[]): boolean {
    return caixas.some(c => this.naCaixa(unidade, c));
  }

  private situacao(r: Resultado | undefined, emCaixas: string[]): Situacao {
    if (!r) return 'pendente';
    if (r.status === 'aberto') return emCaixas.length ? 'na-caixa' : 'outra-unidade';
    if (r.status === 'concluido') return 'concluido';
    if (r.status === 'nao_encontrado') return 'nao-encontrado';
    return 'outro';
  }

  private lerSalvo(): EstadoBot {
    const vazio: EstadoBot = {caixas: [], fila: [], resultados: {}, gastoMs: 0, caixasLidas: {}, atualizarLocalizacao: false};
    try {
      const salvo = JSON.parse(localStorage.getItem(BotSeiService.CHAVE) || 'null');
      if (salvo && Array.isArray(salvo.fila)) {
        // formato das versões de teste no HOM: uma caixa só (e a opção de subunidades)
        if (!Array.isArray(salvo.caixas)) salvo.caixas = salvo.caixa ? [salvo.caixa] : [];
        if (!salvo.caixasLidas) salvo.caixasLidas = salvo.caixaLida ? {[salvo.caixaLida.unidade]: salvo.caixaLida} : {};
        delete salvo.caixa; delete salvo.caixaLida; delete salvo.subunidades;
        return {...vazio, ...salvo};
      }
    } catch { /* sem armazenamento */ }
    return vazio;
  }

  private salvar() {
    try { localStorage.setItem(BotSeiService.CHAVE, JSON.stringify(this.estado())); } catch { /* sem armazenamento */ }
  }

  private dataBr(iso: string | null): string {
    return iso ? iso.split('-').reverse().join('/') : '';
  }

  // 75 s -> "1min 15s"; 3725 s -> "1h 02min"
  private duracao(seg: number): string {
    seg = Math.max(0, Math.round(seg));
    if (!seg) return '—';
    if (seg < 60) return `${seg}s`;
    const h = Math.floor(seg / 3600), m = Math.floor((seg % 3600) / 60), s = seg % 60;
    return h ? `${h}h ${String(m).padStart(2, '0')}min` : `${m}min ${String(s).padStart(2, '0')}s`;
  }
}
